import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Image as ImageIcon, GripVertical } from "lucide-react";

const EMPTY = {
  title: "", subtitle: "", image_url: "", link_url: "",
  position: "both", start_date: "", end_date: "",
  is_active: true, display_order: 0,
};

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetch = () => api.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => {});
  useEffect(() => { fetch(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b }); setEditing(b.id); };

  const save = async () => {
    if (!form.title.trim() || !form.image_url.trim()) return toast.error("Title and image URL are required");
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/banners", form);
        toast.success("Banner created");
      } else {
        const { id, created_at, ...rest } = form;
        await api.put(`/admin/banners/${editing}`, rest);
        toast.success("Banner updated");
      }
      setEditing(null);
      fetch();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/admin/banners/${id}`);
    toast.success("Banner deleted");
    fetch();
  };

  const positionLabel = { homepage: "Homepage", search: "Search Page", both: "Both Pages" };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-banners-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl">Promo Banners</h1>
          <p className="text-sm text-muted-foreground mt-1">Seasonal offers displayed on homepage and search page</p>
        </div>
        <Button onClick={openNew} data-testid="add-banner-btn"><Plus className="w-4 h-4 mr-1" /> New Banner</Button>
      </div>

      {banners.length === 0 ? (
        <Card className="p-12 text-center">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No banners yet. Create your first promo banner to spotlight deals.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {banners.map((b) => (
            <Card key={b.id} className="p-3 flex items-center gap-4" data-testid={`banner-${b.id}`}>
              <div className="w-28 h-16 rounded-lg overflow-hidden bg-muted shrink-0">
                {b.image_url ? (
                  <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-muted-foreground" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{b.title}</span>
                  <Badge variant={b.is_active ? "default" : "secondary"} className="text-[10px]">
                    {b.is_active ? "Active" : "Inactive"}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">{positionLabel[b.position] || b.position}</Badge>
                </div>
                {b.subtitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{b.subtitle}</p>}
                <div className="text-xs text-muted-foreground mt-0.5">
                  {b.start_date && `From ${b.start_date.split("T")[0]}`}
                  {b.end_date && ` to ${b.end_date.split("T")[0]}`}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`edit-banner-${b.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(b.id)} className="text-destructive" data-testid={`delete-banner-${b.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Editor Sheet */}
      <Sheet open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing === "new" ? "New Banner" : "Edit Banner"}</SheetTitle>
            <SheetDescription>Configure the promo banner details</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Summer Mega Sale!" data-testid="banner-title-input" />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Up to 40% off on all tours" />
            </div>
            <div>
              <Label>Image URL *</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="banner-image-input" />
              {form.image_url && (
                <div className="mt-2 aspect-[4/1] rounded-lg overflow-hidden bg-muted">
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div>
              <Label>Link URL (optional)</Label>
              <Input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="/tours or /search?category=hotel" />
            </div>
            <div>
              <Label>Show On</Label>
              <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                <SelectTrigger data-testid="banner-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="homepage">Homepage Only</SelectItem>
                  <SelectItem value="search">Search Page Only</SelectItem>
                  <SelectItem value="both">Both Pages</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date?.split("T")[0] || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date?.split("T")[0] || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Display Order</Label>
              <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="banner-active-switch" />
              <Label>Active</Label>
            </div>
            <Button onClick={save} disabled={saving} className="w-full" data-testid="save-banner-btn">
              {saving ? "Saving..." : editing === "new" ? "Create Banner" : "Update Banner"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
